// src/IncomingInventory.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Collapse,
  Fade,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { addDays, format as fmt, isPast, isWithinInterval, parseISO } from 'date-fns';
import { useHeaderConfig } from './components/SidebarAndHeaderBar';
import CalendarView from './CalendarView';
import PageContainer from './components/common/PageContainer';
import ViewHeader from './components/ViewHeader';
import IncomingInventorySkeleton from './Skeletons/IncomingInventorySkeleton';
import {
  incomingInventoryColumns,
  incomingInventoryLayout,
  incomingInventoryMinWidth,
  incomingInventoryPriorities,
} from './incomingInventoryColumns';
import useColumnPriority from './components/common/useColumnPriority';
import useContainerWidth from './components/common/useContainerWidth';
import TableViewport from './components/common/TableViewport';
import MobileRecordCard, { KARTE_TITEL_BASIS_SX } from './components/common/MobileRecordCard';
import Pill from './components/common/Pill';
import KpiRow, { KpiCell } from './components/common/KpiRow';
import StatTile from './components/common/StatTile';
import TableFooterSimple from './components/common/TableFooterSimple';
import Panel from './components/common/Panel';
import EmptyState from './components/common/EmptyState';
import RefreshButton from './components/common/RefreshButton';
import ListToolbar from './components/common/ListToolbar';
import CollapsibleSection from './components/common/CollapsibleSection';
import BottomSheetSelect from './components/common/BottomSheetSelect';
import BottomSheetMultiSelect from './components/common/BottomSheetMultiSelect';
import FilterPillRow from './components/common/FilterPillRow';
import useListQuery from './components/common/listQuery/useListQuery';
import { tagImBereich, vorwahlBereich } from './components/common/DateRangeFilter';
import { CONTENT_WIDTH, CONTROL_HEIGHT_SX, hoverOnly, V2, DUR, SP } from './theme/tokens';
import { berlinDateISO, formatDate, formatInt } from './utils/format';

const API = process.env.REACT_APP_API_BASE_URL;

const ROWS_PER_PAGE = 50;

/** Das Breitenmuster dieser Seite — G8b/§2.7: `full`. Der Rueckfall VOR der
 *  ersten Messung ist `CONTENT_WIDTH[MUSTER] ?? CONTENT_WIDTH.reading`
 *  (`CONTENT_WIDTH.full` ist `null`), nie 0 — Hausform `POOverview.jsx:182/821`. */
const MUSTER = 'full';

const GROUP_OPTIONS = [
  { value: 'vendor', label: 'Vendor' },
  { value: 'po', label: 'Purchase Order' },
  { value: 'product', label: 'Product' },
];

const SORT_VALUES = [
  'product_name',
  'vendor_name',
  'po_id',
  'eta',
  'inbound_qty',
  'current_on_hand',
  'reorder_point',
  'reorder_date',
  'estimated_stockout_date',
];

const NUMERIC_COLS = new Set(['inbound_qty', 'current_on_hand', 'reorder_point']);

/** Der Karten-TITEL dieser Seite: der Produktname, zwei Zeilen geklemmt, und
 *  das BEDIENELEMENT (R16 B1, 2026-10-22).
 *
 *  🔴 **Warum ein Link und nicht die Flaeche.** `MobileRecordCard` sagt in
 *  seinem eigenen Vertrag, dass die Karte KEIN Bedienelement ist und `role`/
 *  `tabIndex` am Wurzelknoten nicht traegt (`@useWhen`/`@dontUseFor`); der Titel
 *  ist der Weg hinein. Bis hierher war der Titel ein reines `<Typography>`, und
 *  damit gab es auf dem Telefon — der Hauptflaeche dieser Seite — **kein**
 *  Element im A11y-Baum, das den PO oeffnet (WCAG 2.1.1/4.1.2). Der Zeiger-Tipp
 *  auf die Flaeche bleibt als Komfort.
 *
 *  🔴 **`KARTE_TITEL_BASIS_SX` ist die gemeinsame Hausbasis** (Flaeche +
 *  44-px-Boden auf grobem Zeiger, `MobileRecordCard.jsx`): sie wird gespreadet,
 *  nicht kopiert. `display: '-webkit-box'` ueberschreibt dort `display: 'block'`
 *  — die Zwei-Zeilen-Klemme braucht den Box-Kontext; `position: relative` und
 *  der `::after`-Boden bleiben erhalten. `whiteSpace: 'nowrap'` aus
 *  `POOverview.KARTE_TITEL_SX` wird ABSICHTLICH nicht uebernommen: ein
 *  Produktname ist keine Kennung und darf umbrechen (§2.9). */
const KARTE_NAME_SX = {
  ...KARTE_TITEL_BASIS_SX,
  fontWeight: 700,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

/** E1 (2026-10-06): der Anzeigename des Lieferanten. `shp_vendors.vendor_name`
 *  traegt bei 20 von 32 Lieferanten den SLUG („moins", „pranachai"); der
 *  Anzeigename steht in `metaobject_name` (Backend-`SELECT`, kein Datenfix).
 *  Rueckfall auf den Slug, damit die Flaeche nie leer wird. EINE Stelle fuer
 *  Gruppen, Filter, Suche und Anzeige — sonst laufen sie auseinander. */
export function vendorLabel(r) {
  return r.metaobject_name || r.vendor_name || '';
}

function buildGroups(rows, groupBy) {
  const getKey = (r) => {
    if (groupBy === 'vendor') return vendorLabel(r) || '—';
    if (groupBy === 'po') return r.po_id ? String(r.po_id) : '—';
    return r.product_name || '—';
  };
  const map = new Map();
  rows.forEach((r) => {
    const k = getKey(r);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  });
  return map;
}

function stockoutColor(isoDate) {
  if (!isoDate) return null;
  try {
    const sd = parseISO(isoDate);
    const now = new Date();
    if (isPast(sd)) return V2.errText;
    if (isWithinInterval(sd, { start: now, end: addDays(now, 30) })) return V2.warnText;
  } catch {
    // ignore
  }
  return null;
}

function safeParseISO(iso) {
  try { return parseISO(iso); } catch { return null; }
}

/** Unbekannt ist NICHT 0 (N8.3/P10, OE-8 A): der Halbgeviertstrich, nicht der
 *  Gedankenstrich — `—` liest sich als „kein Wert vorhanden", `–` als „nicht
 *  gemessen". Dieselbe Form tragen die Kacheln der Seite. */
export const UNBEKANNT = '–';

const formatMoney = (value) => {
  // 🔴 `Number(null)` ist **0**, nicht `NaN` (R15 B1). Ohne diese Zeile faellt
  // eine 200-Antwort OHNE `incoming_inventory_amount` in den `null`-Zweig von
  // `:247` und die Kachel behauptet „0 €" — gemessen statt unbekannt. Der
  // `undefined`-Fall ist derselbe: `Number(undefined)` ist `NaN`, aber die
  // Absicht ist „kein Wert", nicht „nicht endlich".
  if (value == null) return UNBEKANNT;
  const n = Number(value);
  if (!Number.isFinite(n)) return UNBEKANNT;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
};

export default function IncomingInventory() {
  const navigate = useNavigate();
  const { setHeaderConfig } = useHeaderConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState([]);
  const [incomingValue, setIncomingValue] = useState(null);
  // 🔴 Zwei Quellen, zwei Lagen (N8.3/P10): faellt `storage-summary` aus, ist die
  // LISTE trotzdem gemessen und umgekehrt. Vorher teilten sich beide einen
  // `Promise.all`-Fehler, und ein Ausfall der Nebenquelle nahm die ganze Liste mit.
  // 🔴 R15-R4 W2: `incomingValue == null` hiess zweierlei — „die Quelle fiel
  // aus" UND „sie antwortete 200 ohne den Wert" —, und der Hinweis behauptete in
  // beiden „did not answer". Das ist in der zweiten Lage falsch: sie HAT
  // geantwortet. Beide Lagen tragen jetzt ihren eigenen Satz (`storageFehlt`
  // trennt sie), der Wert bleibt in beiden `–`.
  // ⚠️ Ein dritter Zustand „laedt" ist hier NICHT noetig und waere toter Kode:
  // `Promise.allSettled` loest erst aus, wenn BEIDE Quellen geantwortet haben,
  // und der Erstaufbau zeigt bis dahin das Geruest (`ersteLadung`). Es gibt
  // also keinen Render, in dem die Kachel steht und die Nebenquelle noch laeuft
  // — gemessen, nicht angenommen (die Probe L.18 mit haengender Nebenquelle
  // blieb im Geruest und fiel).
  const [storageFehlt, setStorageFehlt] = useState(false);
  const [loading, setLoading] = useState(true);
  // 🔴 R15 W6: „nie geladen" ist nicht „laedt gerade". `jeGeladen` faellt im
  // `then` der LISTE (nicht im `finally`) und traegt die Entscheidung, ob das
  // Geruest gezeigt wird — Hausform `POOverview.jsx:737` (`ersteLadung`).
  // Ohne ihn ersetzte jedes Auffrischen die Seite durch das Geruest, und der
  // `loading`-Prop des `RefreshButton` war beim Rendern immer `false`.
  const [jeGeladen, setJeGeladen] = useState(false);
  const [listError, setListError] = useState('');

  const [groupBy, setGroupBy] = useState('vendor');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const toggleGroup = (name) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // ── Vendor options (sorted, derived from all rows) ────────────────────────
  // 🔴 Der Wert ist die `vendor_id`, das Etikett der Anzeigename. Ein Lieferant
  // ohne Anzeigenamen bekommt das Etikett `—` und bleibt wählbar (P13-Fall).
  // 🔴 Steht VOR `dimensionen`: die Facetten-Dimension braucht die Werteliste,
  // sonst nimmt der Hook jeden Wert an (N10.4/P13).
  const vendorOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const id = r.vendor_id;
      if (id == null) return;
      if (!map.has(id)) map.set(id, vendorLabel(r) || '—');
    });
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rows]);

  // ── Der Zustand der Liste lebt in der ADRESSE (§3.1/N10.1) ───────────────
  // Vorher lag er in sechs `useState` (Suche, ETA-Fenster, Lieferant, At risk,
  // Sortierung, Seite): nicht teilbar, beim Neuladen verloren. `useListQuery`
  // ist der EINZIGE Schreiber von `q`/`vendor`/`quick`/`eta_from`/`eta_to`/
  // `sort`/`page` (§3.3) — die Ansicht schreibt keinen davon zusätzlich.
  // 🔴 `group` ist KEIN Parameter (§2.6): eine Ansicht engt nichts ein, bekaeme
  // aber einen Chip in Z3, und „Clear all" setzte sie zurück.
  const dimensionen = useMemo(() => [
    // `single` OHNE `values` — nur so entsteht der Chip „Search: <Begriff>".
    { key: 'q', kind: 'single', label: 'Search' },
    // Die Werte sind die STABILEN Backend-Kennungen, die Etiketten die
    // Anzeigenamen (§10.1 „stabile Backend-Werte, nie Etiketten").
    // 🔴 `values` ist PFLICHT: ohne die Liste nimmt der Hook JEDEN Wert an, und
    // `?vendor=foo` verschwände still statt gemeldet zu werden (N10.4/P13).
    { key: 'vendor', kind: 'multi', label: 'Vendor', values: vendorOptions },
    { key: 'quick', kind: 'quick', label: 'Quick filter', values: [{ value: 'at-risk', label: 'At risk' }] },
    // ETA ist ein DATUMSFILTER, kein Segment (N3.5): er blendet Datensätze aus.
    { key: 'eta', kind: 'date', label: 'ETA' },
  ], [vendorOptions]);

  const query = useListQuery({
    dimensions: dimensionen,
    sort: { default: 'eta', values: SORT_VALUES },
    pageSize: ROWS_PER_PAGE,
  });
  const { state, set, clearAll } = query;

  const sortFeld = state.sort ?? 'eta';
  const sortBy = sortFeld.startsWith('-') ? sortFeld.slice(1) : sortFeld;
  const sortOrder = sortFeld.startsWith('-') ? 'desc' : 'asc';
  const seite = state.page - 1;

  // 🔴 `state.vendor`/`state.quick` sind stabil, solange die Adresse steht
  // (`useListQuery` memoisiert `state`); der leere Rückfall wäre dagegen ein
  // frisches `[]` je Render und liesse jeden `useMemo` darunter neu laufen.
  const vendorFilter = useMemo(() => state.vendor ?? [], [state.vendor]);
  const quickFilter = useMemo(() => state.quick ?? [], [state.quick]);
  const nurAtRisk = quickFilter.includes('at-risk');
  const suchtext = state.q ?? '';

  // ── Fetch ─────────────────────────────────────────────────────────────────
  // 🔴 `allSettled`, nicht `all`: die zwei GETs sind zwei Quellen, und ein
  // Ausfall der einen sagt nichts über die andere (N8.3/P10). Der eine
  // Auffrischweg lädt beide neu (N2.6).
  // 🔴 R15-R4 W3: ein Generationszaehler, kein AbortController. Ohne ihn setzte
  // eine SPAET aufloesende, ueberholte Fehlschlag-Antwort `listError`, nachdem
  // der spaetere Erfolg schon `rows`/`jeGeladen` geschrieben hat — ein „Could
  // not refresh" fuer eine Anfrage, die niemanden mehr interessiert. Die WIRKUNG
  // war vor dem Diff schlimmer (`setRows([])`), die Ursache ist aelter als G8a.
  // ⚠️ EHRICH: heute ist der Fall ueber die Oberflaeche NICHT herstellbar —
  // `Refresh` ist waehrend eines Laufs gesperrt (`RefreshButton.jsx:124`), und
  // „Try again" gibt es nur im Leerzustand, also nie waehrend eines Laufs
  // (gemessen, Probe L.20). Der Zaehler ist die Versicherung fuer den Tag, an
  // dem ein zweiter Aufrufer dazukommt; er kostet vier Zeilen und macht die
  // Zusage „der Zustand gehoert dem juengsten Lauf" explizit.
  const laufRef = useRef(0);

  const ladeDaten = useCallback(() => {
    const lauf = ++laufRef.current;
    setLoading(true);
    setListError('');
    Promise.allSettled([
      axios.get(`${API}/api/incoming_inventory`),
      axios.get(`${API}/api/dashboard/storage-summary`),
    ])
      .then(([invRes, storageRes]) => {
        // Ueberholte Antwort: der Zustand gehoert dem juengeren Lauf.
        if (lauf !== laufRef.current) return;
        if (invRes.status === 'fulfilled') {
          setRows((invRes.value.data || []).map((r, i) => ({ id: i, ...r })));
          // „Erstaufbau gelungen" — der Anker des Gerusts (W6).
          setJeGeladen(true);
        } else {
          // 🔴 R16 C3: ein gescheiterter AUFFRISCHVORGANG wirft die geladene
          // Liste nicht weg. `rows` bleibt stehen — beim ERSTEN Aufbau ist es
          // ohnehin `[]`, es gibt also keinen Fall, in dem das Leeren etwas
          // beitraegt. Die zwei Lagen trennt `jeGeladen` (Hausform
          // `POOverview.jsx:705`), nicht der Fehler allein.
          setListError('Failed to load incoming inventory.');
        }
        if (storageRes.status === 'fulfilled') {
          setIncomingValue(storageRes.value.data?.incoming_inventory_amount ?? null);
          setStorageFehlt(false);
        } else {
          setIncomingValue(null);
          setStorageFehlt(true);
        }
      })
      // Auch der Spinner gehoert dem juengeren Lauf: ein ueberholter Lauf darf
      // ihn nicht loeschen, waehrend der neue noch unterwegs ist.
      .finally(() => {
        if (lauf === laufRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    setHeaderConfig({ title: 'Incoming Inventory', showSearch: true, actions: [] });
    ladeDaten();
  }, [setHeaderConfig, ladeDaten]);

  // ── Calendar events + handlers ────────────────────────────────────────────
  const incomingEvents = useMemo(
    () => rows.filter((r) => r.eta).map((r) => ({ date: new Date(r.eta), resource: r })),
    [rows]
  );
  const reorderEvents = useMemo(
    () => rows.filter((r) => r.reorder_date).map((r) => ({ date: new Date(r.reorder_date), resource: r })),
    [rows]
  );

  const handleIncomingClick = (resource, newDate) => {
    if (newDate) {
      const ds = fmt(newDate, 'yyyy-MM-dd');
      setRows((prev) => prev.map((r) => r.po_id === resource.po_id ? { ...r, eta: ds } : r));
      axios.put(`${API}/api/purchase_orders/${resource.po_id}`, { expected_arrival: ds }).catch(console.error);
    } else {
      navigate(`/purchase_order/${resource.po_id}`);
    }
  };

  const handleReorderClick = (resource, newDate) => {
    if (newDate) {
      const ds = fmt(newDate, 'yyyy-MM-dd');
      setRows((prev) => prev.map((r) => r.po_id === resource.po_id ? { ...r, reorder_date: ds } : r));
      axios.put(`${API}/api/purchase_orders/${resource.po_id}`, { reorder_date: ds }).catch(console.error);
    } else {
      navigate(`/new-purchase-order?sku=${encodeURIComponent(resource.sku)}`);
    }
  };

  // ── KPIs (always based on all rows, not filtered) ─────────────────────────
  // 🔴 Sechs Kacheln, EINE Zeile (§2.5/B1). „Stockout risk" ist KEINE Kachel:
  // die Zahl lebt an der Quick-Filter-Pille „At risk", die exakt dieselbe Menge
  // auswählt (§6.1: „Eine Zahl, die nur eine Kategorie der Liste zählt, gehört
  // als Zähler an die Quick-Filter-Pille dieser Kategorie").
  const kpis = useMemo(() => {
    const now = new Date();

    const totalItems = rows.length;
    const totalUnits = rows.reduce((s, r) => s + (Number(r.inbound_qty) || 0), 0);
    const uniqueVendors = new Set(rows.map(vendorLabel).filter(Boolean)).size;

    // 🔴 „Arriving this week" rechnet SEIT G8a mit demselben Tagesbegriff und
    // derselben Menge wie die Vorwahl „Next 7 days" (Plan-W5/W1): Berliner
    // Kalendertag, sieben Tage einschliesslich heute, EINE Haushilfe. Vorher
    // zählte sie ab dem JETZIGEN ZEITPUNKT über sieben Tage — eine ETA mit
    // heutigem Datum 00:00 fiel heraus, und zwischen 22 und 24 UTC (Sommerzeit)
    // lag der Berliner Tag einen vor dem UTC-Tag, sodass KPI und Filter
    // verschiedene Fenster zeigten.
    const heute = berlinDateISO(now);
    const woche = vorwahlBereich('next7', heute);
    const arrivingThisWeek = rows.filter(
      (r) => r.eta && tagImBereich(berlinDateISO(r.eta), woche)
    ).length;

    const etaDates = rows
      .map((r) => r.eta && safeParseISO(r.eta))
      .filter(Boolean)
      .sort((a, b) => a - b);
    // 🔴 R15 W3: derselbe Tagesbegriff wie Tabelle und Filter — der BERLINER
    // Kalendertag ueber `formatDate` (= `formatBerlinDate`), nicht der lokale
    // von date-fns. `2026-10-27T23:30:00Z` ist in Berlin der 28.10.; die Spalte
    // zeigt seit G8a den 28., die Kachel zeigte im UTC-Browser den 27.
    const earliestEta = etaDates.length ? formatDate(etaDates[0]) : '—';

    const atRisk = rows.filter((r) => stockoutColor(r.estimated_stockout_date) !== null).length;
    const mitPrognose = rows.filter((r) => Boolean(r.estimated_stockout_date)).length;

    return {
      totalItems,
      totalUnits,
      uniqueVendors,
      arrivingThisWeek,
      earliestEta,
      atRisk,
      mitPrognose,
      ohnePrognose: totalItems - mitPrognose,
    };
  }, [rows]);

  // ── Filter ────────────────────────────────────────────────────────────────
  // §2.4: aktiv, wenn eine Grenze gesetzt ist. Verglichen wird der BERLINER
  // Kalendertag der ETA (`berlinDateISO`) — derselbe Tag, den die Tabelle zeigt
  // (`formatDate` = `formatBerlinDate` ohne `dateOnly`). 🔴 NICHT der
  // Y-M-D-Präfix des UTC-Zeitstempels: `2026-10-27T23:30:00Z` ist in Berlin der
  // 28.10., und die Zeile stünde im Filter an einem anderen Tag als in der Spalte.
  const etaBereich = useMemo(() => state.eta ?? { from: null, to: null }, [state.eta]);
  const etaAktiv = Boolean(etaBereich.from || etaBereich.to);

  const filteredRows = useMemo(() => {
    let result = rows;

    // ETA-Datumsfilter (N3.5: blendet Datensätze aus, ist kein Segment).
    // Zeilen ohne ETA fallen heraus — wie bisher, gemessen 0 (§4.1a).
    if (etaAktiv) {
      result = result.filter(
        (r) => r.eta && tagImBereich(berlinDateISO(r.eta), etaBereich)
      );
    }

    // Vendor multi-select — gegen die stabile Kennung, nie gegen das Etikett.
    if (vendorFilter.length > 0) {
      const set_ = new Set(vendorFilter);
      result = result.filter((r) => set_.has(r.vendor_id));
    }

    // Quick-Filter „At risk" — dieselbe Menge wie der Zähler an der Pille.
    if (nurAtRisk) {
      result = result.filter((r) => stockoutColor(r.estimated_stockout_date) !== null);
    }

    // Text search
    const q = suchtext.trim().toLowerCase();
    if (q) {
      result = result.filter((r) =>
        [r.product_name, vendorLabel(r), r.sku, String(r.po_id ?? ''), r.variant_name]
          .some((v) => v && String(v).toLowerCase().includes(q))
      );
    }

    return result;
  }, [rows, vendorFilter, nurAtRisk, suchtext, etaAktiv, etaBereich]);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const dir = sortOrder === 'asc' ? 1 : -1;
      // §2.11: die Vendor-Spalte sortiert nach dem ANGEZEIGTEN Namen, nicht nach
      // dem Slug — sonst widersprechen sich Anzeige und Reihenfolge.
      const wert = (r) => (sortBy === 'vendor_name' ? vendorLabel(r) : r[sortBy]);
      const av = wert(a) ?? '';
      const bv = wert(b) ?? '';
      if (NUMERIC_COLS.has(sortBy)) return (Number(av) - Number(bv)) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
    return copy;
  }, [filteredRows, sortBy, sortOrder]);

  // ── Pagination (flat), then group the paginated slice ─────────────────────
  const paginatedRows = useMemo(
    () => sortedRows.slice(seite * ROWS_PER_PAGE, (seite + 1) * ROWS_PER_PAGE),
    [sortedRows, seite]
  );

  const pagedGroups = useMemo(
    () => buildGroups(paginatedRows, groupBy),
    [paginatedRows, groupBy]
  );

  // All group keys across ALL pages — needed for collapse/expand all
  const allGroupKeys = useMemo(
    () => Array.from(buildGroups(sortedRows, groupBy).keys()),
    [sortedRows, groupBy]
  );
  const allCollapsed = allGroupKeys.length > 0 && allGroupKeys.every((k) => collapsedGroups.has(k));

  const collapseAll = () => setCollapsedGroups(new Set(allGroupKeys));
  const expandAll  = () => setCollapsedGroups(new Set());

  // N10.4: die Seite wird gegen die Trefferzahl geklemmt. Ein geteilter Link auf
  // Seite 4 bei 12 Treffern zeigt sonst eine leere Fläche, die wie „kein
  // Treffer" aussieht.
  const letzteSeite = Math.max(1, Math.ceil(sortedRows.length / ROWS_PER_PAGE));
  useEffect(() => {
    if (state.page > letzteSeite) set('page', letzteSeite);
  }, [state.page, letzteSeite, set]);

  // Die eingeklappten Gruppen gehören zur ANSICHT: wechselt sie, sind die alten
  // Namen bedeutungslos.
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupBy]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortBy === field) set('sort', sortOrder === 'asc' ? `-${field}` : field);
    else set('sort', field);
  };

  const fmtDate = (iso) => formatDate(iso);

  const showVendorCol = groupBy !== 'vendor';
  const showPoCol     = groupBy !== 'po';

  // Die Spalten der geladenen Kopfzeile UND des Ladegeraests — EINE Quelle
  // (`incomingInventoryColumns.js`): Vorgabe `groupBy='vendor'` → 8 Spalten,
  // `groupBy='product'` → 9 (§6 Schritt 1.2, R15 B1 / R16 B1; PR 1 Runde 4).
  // `useMemo`: die Liste ist die Eingabe von `useColumnPriority`; eine frische
  // Referenz je Render liesse den Hook (und damit `visible`) jedes Mal neu laufen.
  const columns = useMemo(
    () => incomingInventoryColumns({ showVendorCol, showPoCol }),
    [showVendorCol, showPoCol]
  );

  // ── Die Breitenmechanik (§2.7/§2.7a, G8b) ────────────────────────────────
  // 🔴 Die MESSUNG sitzt am `liste-tabelle`-Traeger, nicht am `TableViewport`:
  // gemessen wird der RAUM (die rohe Innenbreite des Behaelters), nicht die
  // gedeckelte Tabelle — sonst maesse sich der Deckel selbst (BW4,
  // `POOverview.jsx:812-820`). Der Rueckfall VOR der ersten Messung ist das
  // Muster der Seite (`CONTENT_WIDTH.full` ist `null` → `reading`), nie 0.
  const tabelleRef = useRef(null);
  const { width: behaelterBreite } = useContainerWidth(tabelleRef);
  const messBasis = behaelterBreite ?? CONTENT_WIDTH[MUSTER] ?? CONTENT_WIDTH.reading;
  // 🔴 Die Seite ruft `useColumnPriority` SELBST (Plan W6): `TableViewport`
  // rechnet `visible` intern nur fuer sein Budget und gibt es NICHT heraus —
  // ohne eigenen Aufruf verschwaende keine Spalte, und die Zeilen zeichneten
  // weiter alle. Muster `POOverview.jsx:822-836` (Hook) und `:1548` (Kopf UND
  // Rumpf aus EINER `sichtbar`-Menge).
  // 🔴 R15 W3: der Hook verlangt eine STABILE Array-Identitaet (JSDoc
  // `useColumnPriority`, „Keep `columns` stable"). Ein frischer Aufruf im Render
  // liesse `visible`, `sichtbar`, `sichtbareSpalten` und `layout` bei jedem
  // Render neu laufen — die Seite memoisiert `columns` genau deshalb und brach
  // es eine Zeile spaeter. Dieselbe Memoisierung fuer die abgeleitete
  // Deskriptorliste, an BEIDEN Aufrufstellen.
  const prioritaeten = useMemo(() => incomingInventoryPriorities(columns), [columns]);
  const { visible: sichtbareDeskriptoren } = useColumnPriority({
    columns: prioritaeten,
    containerWidth: messBasis,
  });
  const sichtbar = useMemo(
    () => new Set(sichtbareDeskriptoren.map((c) => c.key)),
    [sichtbareDeskriptoren]
  );
  const sichtbareSpalten = useMemo(
    () => columns.filter((c) => sichtbar.has(c.field)),
    [columns, sichtbar]
  );
  // Budget, Tabellenbreite, Rollschwelle und die Prozentbreiten je Spalte — EINE
  // Rechnung in der Spaltenquelle, die das Geruest mit seiner eigenen Messung
  // genauso fahrt (§2.7a; sonst springt die Tabelle beim Eintreffen der Daten).
  const layout = incomingInventoryLayout(sichtbareSpalten, messBasis);

  // Dieselbe Memoisierung fuer die SICHTBARE Menge: sie reist als
  // `TableViewport.priorities` weiter und ist dort Eingabe eines weiteren
  // `useColumnPriority` (R15 W3, zweite Aufrufstelle).
  const prioritatenSichtbar = useMemo(
    () => incomingInventoryPriorities(sichtbareSpalten),
    [sichtbareSpalten]
  );

  // 🔴 R16 W1: eine Sortierung auf einer Spalte, die der Fit AUSGEBLENDET hat,
  // hat keinen sichtbaren Traeger — `renderSortCell` laeuft nur ueber
  // `sichtbareSpalten`, es gibt dann kein `aria-sort`. Vor G8b standen alle acht
  // Spalten immer da; die Luecke entsteht erst durch das Ausblenden. Mobil gibt
  // es gar keinen Tabellenkopf, also traegt dort IMMER der Satz die Ordnung
  // (Hausform `VendorsOverview.jsx:568-577`, `data-testid="sortiert-nach"`).
  const sortLabel = columns.find((c) => c.field === sortBy)?.label ?? sortBy;
  const sortOhneTraeger = isMobile || !sichtbar.has(sortBy);

  // ── Render helpers (plain functions, not React component refs) ────────────
  const renderSortCell = ({ field, label, align = 'left' }) => (
    <TableCell key={field} align={align} sx={{ width: layout.widthFor(field) }}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortOrder : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // Kopf und Rumpf lesen DIESELBE Menge (`sichtbar`), sonst zeigte der Kopf eine
  // Spalte an, die die Zeilen nicht fuellen (oder umgekehrt) — Hausform
  // `POOverview.jsx:1548-1561`.
  const renderTableHead = () => (
    <TableHead>
      <TableRow>
        {sichtbareSpalten.map(renderSortCell)}
      </TableRow>
    </TableHead>
  );

  const renderDataRow = (r) => {
    const color = stockoutColor(r.estimated_stockout_date);
    return (
      <TableRow
        key={r.id}
        hover
        sx={{ cursor: 'pointer' }}
        onClick={() => r.po_id && navigate(`/purchase_order/${r.po_id}`)}
      >
        <TableCell>
          {/* N9.9: der Produktname ist der Tastaturweg in den PO — vorher öffnete
              die Zeile nur per Maus, und der einzige Link (die PO-Zelle) fehlt,
              wenn die PO-Spalte ausgeblendet ist (`groupBy='po'`).
              🔴 Der Link stoppt die Propagation (W11f): sonst feuert der
              Zeilen-`onClick` ein zweites Mal und navigiert doppelt. */}
          {r.po_id ? (
            <MuiLink
              component={RouterLink}
              to={`/purchase_order/${r.po_id}`}
              underline="hover"
              onClick={(e) => e.stopPropagation()}
              sx={{ fontWeight: 600, color: 'text.primary', display: 'inline-block' }}
            >
              {r.product_name || '—'}
            </MuiLink>
          ) : (
            <Typography variant="body2" fontWeight={600}>{r.product_name || '—'}</Typography>
          )}
          {r.variant_name && (
            <Typography variant="caption" color="text.secondary" display="block">{r.variant_name}</Typography>
          )}
          <Typography variant="caption" color="text.disabled">SKU: {r.sku}</Typography>
        </TableCell>

        {sichtbar.has('vendor_name') && (
          <TableCell>
            <Typography variant="body2">{vendorLabel(r) || '—'}</Typography>
          </TableCell>
        )}

        {sichtbar.has('po_id') && (
          <TableCell>
            {r.po_id ? (
              <MuiLink
                component={RouterLink}
                to={`/purchase_order/${r.po_id}`}
                underline="hover"
                sx={{ fontWeight: 500 }}
                onClick={(e) => e.stopPropagation()}
              >
                {r.po_id}
              </MuiLink>
            ) : '—'}
          </TableCell>
        )}

        {sichtbar.has('eta') && <TableCell>{fmtDate(r.eta)}</TableCell>}
        {sichtbar.has('inbound_qty') && (
          <TableCell align="right">{r.inbound_qty ?? '—'}</TableCell>
        )}
        {sichtbar.has('current_on_hand') && (
          <TableCell align="right">{r.current_on_hand ?? '—'}</TableCell>
        )}
        {sichtbar.has('reorder_point') && (
          <TableCell align="right">{r.reorder_point ?? '—'}</TableCell>
        )}
        {sichtbar.has('reorder_date') && <TableCell>{fmtDate(r.reorder_date)}</TableCell>}
        {sichtbar.has('estimated_stockout_date') && (
        <TableCell>
          {/* 🔴 W2 (N8.3/P10): eine Zeile OHNE Prognose ist UNBEKANNT, nicht
              sicher. Heute stand dort `formatDate(null)` = leer, und leer liest
              sich wie „kein Risiko" (gemessen 20 von 56 Zeilen). */}
          {r.estimated_stockout_date ? (
            <>
              <Typography variant="body2" sx={color ? { color } : {}}>
                {fmtDate(r.estimated_stockout_date)}
              </Typography>
              {/* 🔴 G8b/W5: „At risk" war am Desktop NUR die Farbe der Zelle —
                  „Status nur über Farbe" (Regel 22 §2 Punkt 2, N11.3). Dieselbe
                  Pille wie auf der Karte trägt den Text; Datum und Pille stehen
                  UNTEREINANDER, damit die Zelle nur das breitere der beiden
                  fasst (die Spalte bleibt `kind: 'date'`, ≤ 160 px, PW7). */}
              {color && (
                <Pill tone="err" sx={{ mt: SP.chip }}>At risk</Pill>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">No forecast</Typography>
          )}
        </TableCell>
        )}
      </TableRow>
    );
  };

  // 🔴 R16 C1 (2026-10-06): der Kopf steht VOR dem Lade-Zweig und wird in JEDEM
  // Zustand gerendert. Vorher kehrte der Ladezustand in ein Skelett zurueck, das nur
  // graue Balken trug — und die mobile App-Leiste schweigt seit Befund 5(i), also stand
  // auf der Seite waehrend des Ladens GAR KEIN Titel (weder Text noch Leiste).
  const kopf = (
    <ViewHeader
      title="Incoming Inventory"
      breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Incoming Inventory' }]}
    />
  );

  // ── Loading ───────────────────────────────────────────────────────────────
  // 🔴 R15 W6: das Geruest gehoert dem ERSTEN Aufbau („noch nie erfolgreich
  // geladen"), nicht jedem Ladevorgang. Beim Auffrischen bleiben Leiste,
  // Kacheln und Zeilen stehen; `loading` traegt dann nur noch den Spinner am
  // `RefreshButton` und `aria-busy` am Daten-Panel. Ein „Try again" nach
  // gescheitertem Erstaufbau zeigt wieder das Geruest — `jeGeladen` faellt
  // erst im `then`, genau wie in `POOverview.jsx:737`.
  const ersteLadung = loading && !jeGeladen;
  if (ersteLadung) {
    return (
      <Fade in timeout={DUR.ui}>
        <Box>
          {kopf}
          <IncomingInventorySkeleton columns={columns} />
        </Box>
      </Fade>
    );
  }

  // ── Die Bedienelemente der Leiste (§2.2/§2.3) ─────────────────────────────
  // N8.5/P9: im Fehler des ERSTEN Aufbaus ist nichts geladen — Suche, Facette,
  // Z2 und der Filters-Knopf sind gesperrt, statt auf eine leere Liste zu wirken.
  // 🔴 R16 C3: die Sperre haengt an „nichts geladen", nicht am Fehler. Ein
  // gescheitertes AUFFRISCHEN laesst die zuletzt geladene Liste stehen; sie zu
  // sperren naehme dem Nutzer die Ansicht, in der er gerade arbeitet (Hausform
  // `POOverview.jsx:705`). Beide Alerts schliessen einander aus.
  const sperre = Boolean(listError) && !jeGeladen;
  const auffrischFehler = Boolean(listError) && jeGeladen && paginatedRows.length > 0;

  const facetten = [
    <BottomSheetMultiSelect
      key="vendor"
      desktopForm="pill"
      label="Vendor"
      value={vendorFilter}
      onChange={(v) => set('vendor', v)}
      options={vendorOptions}
      disabled={sperre}
    />,
  ];

  // Z2: EIN Quick-Filter. 🔴 `alwaysShow` ist Pflicht — ohne ihn gibt
  // `FilterPillRow` am Desktop `null` zurück und Z2 wäre leer (`:175`).
  // 🔴 W2: der `title` sagt, WORAUF sich die Zahl bezieht — ohne ihn läse sich
  // „At risk 3" als Vollurteil über alle Zeilen, obwohl ein Teil gar keine
  // Prognose hat.
  const quickReihe = (
    <FilterPillRow
      multi
      alwaysShow
      options={[{
        value: 'at-risk',
        label: 'At risk',
        // 🔴 R15 W2: bei ausgefallener Liste ist `rows` geleert, `atRisk` also
        // 0 — die Pille behauptete dann eine SICHERE Null („At risk 0",
        // „0 of 0 items have a forecast"), waehrend die Kacheln daneben `–`
        // zeigen. Dieselbe Form wie der Trefferzaehler in der Leiste
        // (`:833` `count={sperre ? undefined : …}`): im Fehlerzustand traegt
        // die Pille keine Zahl und keinen Titel, der Leerzustand spricht.
        // 🔴 R16 W3: EIN Wort fuer dieselbe Menge (`rows.length`) — der Titel
        // folgt dem Zaehler („incoming items"), nicht „lines".
        count: sperre ? undefined : kpis.atRisk,
        title: sperre ? undefined : `At risk: estimated stockout in the past or within 30 days. `
          + `${kpis.atRisk} of ${kpis.mitPrognose} items have a forecast; `
          + `${kpis.ohnePrognose} items have no forecast and are not counted.`,
      }]}
      value={quickFilter}
      onChange={(v) => set('quick', v)}
      disabled={sperre}
    />
  );

  // 🔴 Der Inhalt von `moreFilters.criteria` ist GENANNT, nicht offengelassen:
  // die Facette steht NUR ab `sm` inline (`ListToolbar.jsx:325`), ohne Eintrag
  // wäre der Lieferantenfilter mobil unerreichbar und das Blatt (P17) hätte kein
  // Kriterium. Das ETA-Kriterium ist ein Datumsfilter (N3.5), kein Segment.
  const blattKriterien = [
    {
      key: 'vendor',
      kind: 'select',
      label: 'Vendor',
      multiple: true,
      value: vendorFilter,
      onChange: (v) => set('vendor', v),
      options: vendorOptions,
      // N11.14: der BENANNTE Leerwert eines Kriteriums ohne Wert.
      emptyLabel: 'Any',
      disabled: sperre,
    },
    {
      key: 'eta',
      kind: 'date',
      label: 'ETA',
      value: etaBereich,
      onChange: (v) => set('eta', v),
      // §2.4 Punkt 3: „Overdue" ZUERST — es ist die dringlichste Gruppe
      // (gemessen 29 von 56 Zeilen mit ETA vor heute, §4.1a). Die fünf
      // Hausvorwahlen bleiben unverändert; diese Liste ist die der ETA-Achse.
      presets: [
        { key: 'overdue', label: 'Overdue' },
        { key: 'next7', label: 'Next 7 days' },
        { key: 'next14', label: 'Next 14 days' },
        { key: 'next30', label: 'Next 30 days' },
        { key: 'custom', label: 'Custom' },
      ],
      disabled: sperre,
    },
  ];

  // N4.6: die BLATT-Kriterien mit Wert — nicht `query.chips.length` (das zählte
  // die Suche mit, die kein Blatt-Kriterium ist).
  const blattAktiv = (vendorFilter.length > 0 ? 1 : 0) + (etaAktiv ? 1 : 0);

  // §2.6: „Group by" ist eine ANSICHT, kein Filter — sie steht im `view`-Slot
  // (zwischen Trefferzähler und Auffrischen) und nur am Desktop. Mobil entfällt
  // sie; die mobile Form gruppiert nach Lieferant.
  const viewKnoten = isMobile ? undefined : (
    <Stack direction="row" spacing={SP.tight} alignItems="center">
      <BottomSheetSelect
        label="Group by"
        value={groupBy}
        onChange={setGroupBy}
        options={GROUP_OPTIONS}
        desktopForm="pill"
      />
      <IconButton
        size="small"
        onClick={allCollapsed ? expandAll : collapseAll}
        aria-label={allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
        sx={{ ...CONTROL_HEIGHT_SX }}
      >
        {allCollapsed
          ? <UnfoldMoreIcon fontSize="small" />
          : <UnfoldLessIcon fontSize="small" />}
      </IconButton>
    </Stack>
  );

  // ── Leerzustände (§2.10) ──────────────────────────────────────────────────
  // Drei Lagen, drei Gründe (§8): keine Daten, kein Treffer, Fehler. Die Suche
  // ist ein eigener Fall des Nichttreffers — sie bekommt das Begriff-Echo und
  // „Clear search".
  const nurSuche = Boolean(suchtext.trim());
  const hatFilter = vendorFilter.length > 0 || nurAtRisk || etaAktiv;
  // 🔴 Die Reihenfolge ist die Aussage: der Fehler des ERSTEN Aufbaus schlägt
  // alles (die Liste ist UNBEKANNT, nicht leer), dann „gar keine Zeilen", dann
  // „keine Treffer". Ein `rows.length ? 'no-match' : 'no-data'` setzte den
  // Leerzustand auch dann, wenn Zeilen da sind — die Tabelle verschwände hinter
  // „keine Treffer".
  // 🔴 R16 C3: ein gescheitertes AUFFRISCHEN ist KEIN Fehler-Leerzustand, solange
  // die zuletzt geladenen Zeilen stehen — dann nennt der Hinweis im Panel den
  // Fehlschlag. Stehen KEINE Zeilen (Erstaufbau gescheitert ODER letzte Ladung
  // war leer), ist der Fehler-Leerzustand der einzige Ort, an dem der Fehlschlag
  // sichtbar wird (R15-R4 W1).
  // 🔴 R15-R4 W1: `!jeGeladen` allein liess die Nullzeilen-Lage still. War die
  // letzte erfolgreiche Ladung LEER und scheitert danach das Auffrischen, stand
  // hier `no-data` — die Seite behauptete „No incoming inventory yet", also eine
  // Aussage ueber die Welt, die gerade nicht gemessen wurde. `rows.length === 0`
  // nimmt diese Lage mit: es gibt keine sichtbaren Zeilen, hinter denen der
  // Fehlschlag verschwinden koennte. ⚠️ Bewusst NICHT mitgenommen: „Zeilen da,
  // Filter blendet alle aus" (`no-match`). Dort ist der Auffrisch-Hinweis
  // NICHT sichtbar — er verlangt sichtbare Zeilen (`POOverview.jsx:1209`),
  // weil sein Satz sonst selbst falsch wäre: er lautet „The last loaded rows
  // are still shown", und blendet ein Filter alle Zeilen aus, sind sie eben
  // nicht gezeigt. (R16-R3 C6: hier stand vorher, der Alert stehe dort im
  // Panel — das widerlegte sich in der eigenen Klammer.)
  const leerGrund = (listError && (!jeGeladen || rows.length === 0))
    ? 'error'
    : (rows.length === 0 ? 'no-data' : (filteredRows.length === 0 ? 'no-match' : null));

  const leerzustand = (
    <EmptyState
      reason={leerGrund}
      {...(leerGrund === 'no-match' && nurSuche
        ? {
          query: suchtext.trim(),
          noun: 'incoming item',
          searchedIn: 'product, vendor, SKU, PO',
          onClearSearch: () => set('q', null),
        }
        : {})}
      message={
        leerGrund === 'error'
          ? 'Could not load incoming inventory.'
          : leerGrund === 'no-data'
            ? 'No incoming items yet.'
            : (nurSuche ? undefined : 'No incoming items match these filters.')
      }
      detail={
        leerGrund === 'no-data'
          ? 'Open purchase orders will appear here.'
          : undefined
      }
      {...(leerGrund === 'error' ? { onRetry: ladeDaten } : {})}
      {...(leerGrund === 'no-match' && !nurSuche && hatFilter
        ? { onClearFilters: clearAll }
        : {})}
      {...(leerGrund === 'no-match' && nurSuche && hatFilter
        ? { onClearFilters: clearAll }
        : {})}
    />
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Fade in timeout={DUR.ui}>
      <Box>
        <Box data-testid="liste-kopf">{kopf}</Box>

        {/* 🔴 §2.7: `width="full"` — die Seite ist ein reiner Arbeitsvorrat; ihr
            Deckel ist das Spaltenbudget (§2.7a), nicht das Lesemuster 1280. Das
            Gerüst zieht im selben Commit mit (§2.7 erster Punkt, G2-Lehre). */}
        <PageContainer width={MUSTER} sx={{ pb: SP.xl }}>
          <Stack spacing={SP.section}>
            {/* §2.10 / R16 C2: der GRUND der Sperre steht sichtbar ÜBER der
                Leiste — in der Hausform (`ListenMuster.jsx:561-565`,
                `POOverview.jsx:1181-1196`). Der Satz sagt etwas anderes als der
                Leerzustand der Liste: der `EmptyState` nennt, WAS fehlschlug
                („Could not load incoming inventory."), dieser `Alert` nennt,
                WARUM die Bedienelemente gesperrt sind. Deshalb `warning` statt
                `error`, ohne den Fehlertext davor und mit dem Marker, den der
                gemeinsame Helfer der Listensuite liest (`tests/listen/helfer.ts`).
                Gilt NUR fuer den Erstaufbau — ein gescheitertes Auffrischen
                sperrt nichts (R16 C3). */}
            {sperre && (
              <Alert
                severity="warning"
                sx={{ mb: SP.card }}
                data-testid="erster-aufbau-gesperrt"
              >
                Search and filters are locked until the list loads. Use Try again below.
              </Alert>
            )}

            {/* ── KPI Row: EINE Zeile, SECHS Kacheln (§2.5/B1) ─────────────── */}
            <Box data-testid="liste-kpi">
              <KpiRow>
                <KpiCell>
                  <StatTile
                    label="Incoming value"
                    value={storageFehlt ? UNBEKANNT : formatMoney(incomingValue)}
                    // 🔴 R16 C1: der Grund haengt am WERT, nicht nur am Ausfall.
                    // Eine 200-Antwort OHNE `incoming_inventory_amount` setzt
                    // `incomingValue` auf `null`; `formatMoney(null)` liefert `–`
                    // (R15 B1) — ohne diesen Zweig aber ohne jede Erklaerung, und
                    // „nicht gemessen" war von „kein Wert" nicht zu unterscheiden.
                    // Wortlaut = Hausform (`ProductDetail.jsx:629`).
                    // 🔴 R15-R4 W2: der AUSFALLSATZ gilt nur noch im Ausfall. Die
                    // 200-Antwort ohne Wert sagt jetzt, was sie ist — sie hat
                    // geantwortet, sie trug nur nichts.
                    hint={
                      storageFehlt
                        ? 'Not loaded — the storage summary did not answer.'
                        : (incomingValue == null
                          ? 'No value in the storage summary.'
                          : undefined)
                    }
                    labelTooltip="SUM(inbound qty × cost per item) — same as Home Dashboard"
                  />
                </KpiCell>
                <KpiCell>
                  <StatTile
                    label="Total items"
                    value={sperre ? UNBEKANNT : kpis.totalItems}
                    hint="incoming items"
                  />
                </KpiCell>
                <KpiCell>
                  <StatTile
                    label="Arriving this week"
                    value={sperre ? UNBEKANNT : kpis.arrivingThisWeek}
                    hint="next 7 days"
                  />
                </KpiCell>
                <KpiCell>
                  <StatTile
                    label="Total inbound units"
                    value={sperre ? UNBEKANNT : formatInt(kpis.totalUnits)}
                  />
                </KpiCell>
                <KpiCell>
                  <StatTile
                    label="Unique vendors"
                    value={sperre ? UNBEKANNT : kpis.uniqueVendors}
                  />
                </KpiCell>
                <KpiCell>
                  <StatTile
                    label="Next ETA"
                    value={sperre ? UNBEKANNT : kpis.earliestEta}
                  />
                </KpiCell>
              </KpiRow>
            </Box>

            {/* ── Daten-Panel ────────────────────────────────────────────── */}
            {/* 🔴 R15 W6: `aria-busy` NUR beim Auffrischen — beim ersten Aufbau
                steht das Geruest, dann gibt es dieses Panel nicht (Hausform
                `POOverview.jsx:1177`). */}
            <Panel pad="none" aria-busy={loading || undefined} data-testid="liste-panel">
              <Box sx={{ px: SP.card, pt: SP.sm, pb: 0 }}>
                {/* 🔴 R16 C3: der Fehler des AUFFRISCHENS ist NICHT der des
                    ersten Aufbaus. Die Zeilen stehen, die Bedienelemente bleiben
                    bedienbar — also nennt dieser Hinweis den Fehlschlag UND den
                    Weg zurueck, statt eine Sperre zu behaupten, die es nicht
                    gibt (Hausform `POOverview.jsx:1209-1217`). Das dritte
                    Gatter ist woertlich uebernommen: der Satz verlangt SICHTBARE
                    Zeilen, sonst waere er falsch. */}
                {auffrischFehler && (
                  <Alert
                    severity="warning"
                    sx={{ mb: SP.card }}
                    data-testid="auffrischen-gescheitert"
                  >
                    Could not refresh the list. The last loaded rows are still shown. Use Refresh to try again.
                  </Alert>
                )}
                <ListToolbar
                  shortcut="/"
                  search={{
                    value: suchtext,
                    onChange: (v) => set('q', v),
                    onCommit: (v) => set('q', v),
                    placeholder: 'Search product, vendor, SKU, PO',
                    // P5: der zugängliche Name beginnt mit „Search".
                    label: 'Search incoming inventory',
                    inputRole: 'searchbox',
                    disabled: sperre,
                    sx: { flex: 1, minWidth: 0 },
                  }}
                  facets={facetten}
                  moreFilters={{
                    disabled: sperre,
                    criteria: blattKriterien,
                    resultCount: sperre ? undefined : filteredRows.length,
                    activeCount: blattAktiv,
                    onClearAll: clearAll,
                    label: 'Filters',
                    title: 'Filters',
                  }}
                  quickFilters={quickReihe}
                  // §4.2/N4.5: im Fehler des ERSTEN Aufbaus trägt die Leiste
                  // keinen Zähler — der Leerzustand spricht.
                  count={sperre ? undefined : {
                    noun: 'incoming items',
                    n: filteredRows.length,
                    m: rows.length,
                    // Der Endpunkt kappt nicht (`products_api.py` `fetchall()`).
                    unknownTotal: false,
                  }}
                  view={viewKnoten}
                  refresh={(
                    <RefreshButton
                      ariaLabel="Refresh incoming inventory"
                      onClick={ladeDaten}
                      loading={loading}
                    />
                  )}
                  query={{ chips: query.chips, clearAll }}
                >
                  {/* N10.4/P13: ein UNBEKANNTER Wert (`?vendor=foo`) wird
                      verworfen UND EINMAL gemeldet — stilles Ignorieren ist der
                      Defekt. Hausmarke `ignoriert-hinweis`. */}
                  {query.ignoredValues.length > 0 && (
                    <Alert
                      severity="info"
                      data-testid="ignoriert-hinweis"
                      sx={{ mx: SP.card, mt: SP.tight }}
                    >
                      {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
                    </Alert>
                  )}

                  {/* R16 W1: der Traeger der Sortierung, wenn der Tabellenkopf
                      ihn nicht zeigen kann (Spalte ausgeblendet, oder mobil gar
                      keine Tabelle). EINE Marke, die `messeP18` schon kennt.
                      🔴 R16 K2 (2026-10-22): NICHT im Leerzustand. Der Satz
                      ordnet Zeilen; ohne eine einzige Zeile behauptet er eine
                      Ordnung ueber nichts. Mobil war er immer da, weil
                      `sortOhneTraeger` dort unbedingt wahr ist — auf dem
                      Desktop greift der Zweig ohnehin nur bei ausgeblendeter
                      Spalte, und dann stehen Zeilen. */}
                  {sortOhneTraeger && !leerGrund && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      data-testid="sortiert-nach"
                      sx={{ display: 'block', mt: SP.tight }}
                    >
                      {`Sorted by ${sortLabel}${sortOrder === 'asc' ? '' : ', descending'}.`}
                    </Typography>
                  )}

                  {/* Inhalt — mobil `MobileRecordCard` mit Vendor-Gruppenköpfen
                      (§2.9), Desktop die gruppierte Tabelle. */}
                  {isMobile ? (
                    <Box sx={{ mt: SP.tight }} data-testid="liste-karte">
                      {leerGrund ? leerzustand : (
                        Array.from(buildGroups(paginatedRows, 'vendor').entries()).map(([groupName, items]) => {
                          // Dieselbe Zahl wie der Desktop-Kopf: die Zeilen der
                          // Seitenscheibe unter dieser Gruppe (Changelog nennt es).
                          const offenePos = new Set(items.map((r) => r.po_id).filter(Boolean)).size;
                          return (
                            <Box key={groupName}>
                              {/* N11.4 — Gruppenkopf mobil: Name + Anzahl-Pille +
                                  (bei `vendor`) Zahl offener POs, dieselbe Form wie
                                  der Desktop-Kopf. 🔴 STATISCH: mobil gibt es kein
                                  „Collapse all", die Köpfe sind keine Schalter —
                                  darum KEIN `IconButton` und kein `onClick`.
                                  🔴 G8b-Nachbesserung 4 (2026-10-22): die zwei
                                  Etiketten sind `Pill`, nicht MUI `Chip` — eine
                                  Chip-Zeile wird GANZ migriert (`Pill.jsx`
                                  Docstring), und mobil wie Desktop tragen
                                  dieselbe Form. `nowrap`, weil „1 item" eine
                                  kurze Marke ist und nicht umbrechen darf.
                                  🔴 Die Pille ist breiter als der Chip, den sie
                                  ersetzt (27,5 statt 18 px hoch, `FS.sm` statt
                                  `FS.xxs`). Auf dem Telefon gibt es KEINEN
                                  waagerechten Bildlaufbehälter — ein Ueberlauf
                                  waere also einer der ganzen Seite. Darum
                                  bricht die Zeile hier um, statt zu schieben:
                                  `flexWrap` + `useFlexGap` (letzteres, weil
                                  `spacing` sonst als Rand je Kind umgesetzt
                                  wird und die zweite Zeile einrueckte). */}
                              <Box sx={{
                                px: SP.card, py: SP.tight, bgcolor: 'action.hover',
                                borderTop: '1px solid', borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}>
                                <Stack direction="row" spacing={SP.chip} alignItems="center" useFlexGap flexWrap="wrap">
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    {groupName}
                                  </Typography>
                                  <Pill nowrap data-testid="gruppe-anzahl">
                                    {`${items.length} item${items.length !== 1 ? 's' : ''}`}
                                  </Pill>
                                  {offenePos > 0 && (
                                    <Pill tone="brand" bordered nowrap>
                                      {`${offenePos} PO${offenePos !== 1 ? 's' : ''}`}
                                    </Pill>
                                  )}
                                </Stack>
                              </Box>
                              <Stack spacing={0}>
                                {items.map((r) => (
                                  <MobileRecordCard
                                    key={r.id}
                                    onClick={() => r.po_id && navigate(`/purchase_order/${r.po_id}`)}
                                    title={(
                                      <>
                                        {/* Titel max. zwei Zeilen; die Variante
                                            bleibt darunter und kann die ETA nicht
                                            mehr einengen (§2.9, Kartenbefund).
                                            🔴 R16 B1: der Name ist der
                                            Tastatur-/Screenreader-Weg in den PO
                                            (Hausform `POOverview.jsx:1381`); die
                                            Kartenflaeche bleibt Komfort. */}
                                        {r.po_id ? (
                                          <MuiLink
                                            component={RouterLink}
                                            to={`/purchase_order/${r.po_id}`}
                                            variant="body2"
                                            color="text.primary"
                                            underline="hover"
                                            onClick={(e) => e.stopPropagation()}
                                            sx={KARTE_NAME_SX}
                                          >
                                            {r.product_name || '—'}
                                          </MuiLink>
                                        ) : (
                                          <Typography variant="body2" fontWeight={700} sx={KARTE_NAME_SX}>
                                            {r.product_name || '—'}
                                          </Typography>
                                        )}
                                        {r.variant_name && (
                                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {r.variant_name}
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                    // N11.5/P15: die Kennung, monospace, nie gekuerzt
                                    // (der Baustein traegt `whiteSpace: nowrap`).
                                    id={r.sku}
                                    slotOrder="chips-first"
                                    // 🔴 B4: OHNE `fieldTestIds` stempelt die Karte
                                    // KEIN `data-testid` (`MobileRecordCard.jsx:311`),
                                    // und P15/P20 faenden null Karten. `r.id` ist der
                                    // INDEX und als Marke unbrauchbar (nach jedem
                                    // Filter zeigt `karte-kennung-3` eine andere Zeile).
                                    fieldTestIds={{
                                      // 🔴 R15 W1 (2026-10-22): die Kartenansicht
                                      // braucht den Titel-Stempel, sonst findet
                                      // `messeP18` (`helfer.ts`) keine Karte und
                                      // meldet `grundGeladen: false`. Hausform
                                      // `VendorsOverview.jsx:596` (statische Marke).
                                      title: 'karte-titel-incoming',
                                      id: `karte-kennung-${r.po_id}-${r.sku}`,
                                      chips: `karte-status-${r.po_id}-${r.sku}`,
                                      meta: `karte-meta-${r.po_id}-${r.sku}`,
                                    }}
                                    chips={(
                                      <>
                                        {/* 🔴 KEIN `data-testid="gruppe-anzahl"` hier
                                            (R15 C3 / R16 K1, 2026-10-23): der Anker
                                            gehoert an die zwei ANZAHL-Pillen der
                                            Gruppenkoepfe. Stuende er auch auf der
                                            Kennung, waere `messeP15`s Rueckfall auf
                                            `.MuiChip-label` (`helfer.ts`) tot — er
                                            greift nur bei 0 markierten Pillen. */}
                                        {r.po_id && (
                                          <Pill nowrap>{r.po_id}</Pill>
                                        )}
                                        {/* N11.3: DIESELBE Form wie in der Desktop-Zeile
                                            (Text traegt die Aussage, nicht nur Farbe). */}
                                        {stockoutColor(r.estimated_stockout_date) && (
                                          <Pill tone="err">At risk</Pill>
                                        )}
                                      </>
                                    )}
                                    meta={(
                                      <Stack spacing={SP.chip}>
                                        <Typography variant="caption" display="block">
                                          ETA: {fmtDate(r.eta)}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Inbound: {r.inbound_qty ?? '—'} units
                                        </Typography>
                                        {/* W2: unbekannt ist nicht sicher — „No forecast"
                                            statt einer leeren Zeile. */}
                                        <Typography variant="caption" display="block">
                                          Est. Stockout: {r.estimated_stockout_date
                                            ? fmtDate(r.estimated_stockout_date)
                                            : 'No forecast'}
                                        </Typography>
                                      </Stack>
                                    )}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  ) : (
                    <Box ref={tabelleRef} data-testid="liste-tabelle">
                      {/* 🔴 §2.7: `TableViewport` um Tabelle UND Fußzeile.
                          `priorities` schaltet den Melde-Kanal ein (und damit
                          `SEARCH_MAX` als Suchdeckel, NW4.2 — darum KEIN eigenes
                          `searchMax`, Hausform `POOverview.jsx:1501-1523`); die
                          rohe Behälterbreite reist als `containerWidth` mit.
                          `stickyFirstColumn`/`edgeHint` sind N9.6 und wirken erst
                          unterhalb der Rollschwelle (dort rollt der Container).
                          `sx` ist das ANWENDEN von `min(Behälter, Budget)` — ohne
                          sie lösten die Prozentbreiten gegen den Behälter statt
                          gegen `tabellenIst` auf, und bei 2560 würde `identity`
                          436,8 px > `COLUMN_MAX.identity.max` 360 (PW7). */}
                      <TableViewport
                        footerPadPx={0}
                        priorities={prioritatenSichtbar}
                        containerWidth={messBasis}
                        stickyFirstColumn
                        edgeHint
                        sx={layout.tabellenMax > 0 ? { maxWidth: layout.tabellenMax } : undefined}
                      >
                      {leerGrund ? (
                        /* N8.4/P33: der Tabellenkopf bleibt im Leerzustand
                           stehen — der `EmptyState` steht im `TableBody` über
                           alle Spalten, nicht statt der Tabelle. */
                        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: incomingInventoryMinWidth(sichtbareSpalten) }}>
                          {renderTableHead()}
                          <TableBody>
                            <TableRow>
                              <TableCell colSpan={sichtbareSpalten.length} sx={{ border: 0, py: SP.lg }}>
                                {leerzustand}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      ) : (
                        Array.from(pagedGroups.entries()).map(([groupName, items]) => {
                          const isCollapsed = collapsedGroups.has(groupName);

                          // Count distinct open POs in this group (shown only when groupBy=vendor)
                          const openPoCount = groupBy === 'vendor'
                            ? new Set(items.map((r) => r.po_id).filter(Boolean)).size
                            : null;

                          return (
                            <Box key={groupName}>
                              {/* Group header */}
                              <Box
                                onClick={() => toggleGroup(groupName)}
                                sx={{
                                  px: SP.card,
                                  py: SP.tight,
                                  bgcolor: 'action.hover',
                                  borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                  borderTop: (t) => `1px solid ${t.palette.divider}`,
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  ...hoverOnly({ bgcolor: 'action.selected' }),
                                }}
                              >
                                <Stack direction="row" spacing={SP.chip} alignItems="center">
                                  <IconButton
                                    size="small"
                                    tabIndex={-1}
                                    aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} group ${groupName}`}
                                    sx={{
                                      p: 0.25,
                                      transition: 'transform 0.2s',
                                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                    }}
                                  >
                                    <ExpandMoreIcon fontSize="small" />
                                  </IconButton>

                                  <Typography variant="subtitle2" fontWeight={700}>
                                    {groupName}
                                  </Typography>

                                  <Pill nowrap data-testid="gruppe-anzahl">
                                    {`${items.length} item${items.length !== 1 ? 's' : ''}`}
                                  </Pill>

                                  {openPoCount !== null && openPoCount > 0 && (
                                    <Pill tone="brand" bordered nowrap>
                                      {`${openPoCount} PO${openPoCount !== 1 ? 's' : ''}`}
                                    </Pill>
                                  )}
                                </Stack>
                              </Box>

                              {/* Group table — animated collapse */}
                              <Collapse in={!isCollapsed} unmountOnExit>
                                {/* Welle 2 PR 3a: feste Spaltenbreiten aus der EINEN
                                    Quelle (`incomingInventoryColumns.js`) — dieselbe
                                    Verteilung wie im Ladegeruest, damit der Kopf beim
                                    Eintreffen der Daten nicht springt. */}
                                {/* 🔴 R15 B1: die ROLLSCHWELLE rechnet ueber die
                                    SICHTBAREN Spalten — wie Leerzustand
                                    (`incomingInventoryMinWidth(sichtbareSpalten)`)
                                    und Geruest. Mit `columns` (allen acht) war sie
                                    888 statt 504: `table-layout: fixed` setzte die
                                    Tabelle auf `max(Behaelter, 888)`, die
                                    Prozentbreiten loesten gegen 888 auf, und
                                    `identity` wurde 380,6 px > `COLUMN_MAX.identity.max`
                                    360 — genau der Zustand, den PW7 verbietet. */}
                                <Table size="small" sx={{ tableLayout: 'fixed', minWidth: incomingInventoryMinWidth(sichtbareSpalten) }}>
                                  {renderTableHead()}
                                  <TableBody>
                                    {items.map((r) => renderDataRow(r))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </Box>
                          );
                        })
                      )}
                      {/* Fußzeile: §4.2/N4.3 — KEIN `leftSlot` mehr, der Zähler
                          steht genau einmal (Z1 Desktop / Z3 mobil). Sie sitzt
                          IN `TableViewport` (§2.7), damit sie an derselben Breite
                          hängt wie die Tabelle.
                          🔴 R15 W4 (2026-10-22), am Kode entschieden: die
                          `position: sticky` aus `TableFooterSimple` traegt hier
                          NICHT mehr. Sie klebt am naechsten Scrollport, und das
                          ist jetzt der `TableContainer` (`overflow: auto`) —
                          dessen Hoehe ist inhaltsgetrieben, er scrollt
                          senkrecht gar nicht. Der Klebeeffekt war damit schon
                          vorher wirkungslos (er haette am Fenster geklebt) und
                          ist es jetzt ortsbedingt; die Fusszeile steht am Ende
                          der Liste. Das ist der ausdrueckliche Preis der
                          Breitenbindung aus §2.7 und steht im Changelog. */}
                      <Box data-testid="liste-fuss">
                        <TableFooterSimple
                          count={filteredRows.length}
                          page={seite}
                          onPageChange={(p) => set('page', p + 1)}
                          rowsPerPage={ROWS_PER_PAGE}
                          stickyOnMobile={false}
                        />
                      </Box>
                      </TableViewport>
                    </Box>
                  )}

                  {/* Fußzeile der KARTENLISTE (dieselbe Marke `liste-fuss`, aber
                      nie gleichzeitig mit der Tabellen-Fußzeile: die zwei Zweige
                      schliessen einander aus). */}
                  {isMobile && (
                    <Box data-testid="liste-fuss">
                      <TableFooterSimple
                        count={filteredRows.length}
                        page={seite}
                        onPageChange={(p) => set('page', p + 1)}
                        rowsPerPage={ROWS_PER_PAGE}
                        stickyOnMobile={false}
                      />
                    </Box>
                  )}
                </ListToolbar>
              </Box>
            </Panel>

            {/* ── Kalender: eingeklappt UNTER dem Daten-Panel (§2.8) ─────── */}
            {/* Heute schob der offene Kalender (≈ 300 px) die erste Datenzeile
                aus dem 900er-Bildschirm. Die zwei Schreibwege bleiben
                unverändert; nur der Ort und die Falte sind neu. */}
            <CollapsibleSection
              title="Delivery Calendar"
              summary="ETA and reorder dates"
              defaultExpanded={false}
            >
              <CalendarView
                incomingEvents={incomingEvents}
                reorderEvents={reorderEvents}
                onIncomingClick={handleIncomingClick}
                onReorderClick={handleReorderClick}
              />
            </CollapsibleSection>
          </Stack>
        </PageContainer>
      </Box>
    </Fade>
  );
}
